import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { getAssetHierarchyPalette, getContainedButtonSx } from "../assetHierarchyStyles";
import { getAssetDocumentTypeBadge } from "../assetDocumentEnums";
import cmmsMessages from "../cmmsMessages";
import messages from "../messages";
import { deleteAssetDocument, downloadAssetDocument, fetchAssetDocuments } from "../../../fetchers/cmmsAssetDocument";
import { fetchUsers } from "../../../fetchers/user";
import { HideIfNoRole } from "../../../auth/authUi";
import { CMMS_FIELD_ROLES, CMMS_MANAGER_ROLES } from "../cmmsRoles";
import AxionControls from "../../../components/controls/AxionControls";
import AssetDocumentEditDialog from "./AssetDocumentEditDialog";
import AssetDocumentUploadDialog from "./AssetDocumentUploadDialog";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { formatTimestamp } from "../cmmsFormat";
import { CmmsListSkeleton, CmmsErrorState, formatCmmsError } from "./CmmsStates";

const formatFileSize = (bytes) => {
  if (bytes == null) return "";
  if (bytes === 0) return "0 B";
  const units = ["B", "kB", "MB", "GB"];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  return `${Math.round(value * 100) / 100} ${units[exponent]}`;
};

// Icon comes from the stored contentType rather than the filename extension —
// the backend records the real MIME type, so there is nothing to guess at.
const getFileIcon = (contentType) => {
  const type = (contentType ?? "").toLowerCase();
  if (type.startsWith("image/")) return ImageIcon;
  if (type === "application/pdf") return PictureAsPdfIcon;
  if (type.startsWith("text/") || type.includes("word") || type.includes("officedocument")) return DescriptionIcon;
  return InsertDriveFileIcon;
};

export default function AssetDocumentList({ assetNodeId, companyKey, overviewImageDocumentId = null }) {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const queryClient = useQueryClient();
  const operationFailed = intl.formatMessage(messages.documentsOperationFailed);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  const documentsQuery = useQuery(
    ["cmmsAssetDocuments", companyKey, assetNodeId],
    () => fetchAssetDocuments(companyKey, assetNodeId),
    { enabled: Boolean(companyKey && assetNodeId), staleTime: 30000 },
  );

  // Same query key as the work order page, so this is a cache hit whenever users
  // have already been loaded elsewhere in the session.
  const usersQuery = useQuery(["users"], () => fetchUsers(), { staleTime: 300000 });

  const userNameById = useMemo(() => {
    const map = new Map();
    (usersQuery.data ?? []).forEach((user) => {
      map.set(user.id, user.name ?? user.username ?? `#${user.id}`);
    });
    return map;
  }, [usersQuery.data]);

  const documents = (documentsQuery.data ?? [])
    .slice()
    .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());

  const deleteMutation = useMutation({
    mutationFn: (documentId) => deleteAssetDocument(companyKey, assetNodeId, documentId),
  });

  const handleDownload = async (doc) => {
    try {
      await downloadAssetDocument(companyKey, assetNodeId, doc.id, doc.fileName);
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, operationFailed));
    }
  };

  const handleDelete = (doc) => {
    const isOverviewImage = doc.id === overviewImageDocumentId;
    setConfirmation({
      title: intl.formatMessage(messages.documentsDeleteTitle),
      message: intl.formatMessage(
        isOverviewImage ? messages.documentsDeleteOverviewImageMessage : messages.documentsDeleteMessage,
        { title: doc.title },
      ),
      confirmText: intl.formatMessage(cmmsMessages.delete),
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync(doc.id);
          setConfirmation(null);
          await queryClient.invalidateQueries(["cmmsAssetDocuments", companyKey, assetNodeId]);
          // The API cleared the node's pointer along with the document. The machine card reads
          // the pointer off the tree, which would otherwise keep showing a deleted image.
          if (isOverviewImage) {
            await queryClient.invalidateQueries(["cmmsAssetHierarchyTree"]);
          }
          pushNotification(intl.formatMessage(messages.documentsDeleted));
        } catch (error) {
          pushErrorMessage(formatCmmsError(error, operationFailed));
        }
      },
    });
  };

  const isMutating = deleteMutation.isPending || deleteMutation.isLoading;

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5, gap: 1, flexWrap: "wrap" }}
      >
        <Typography variant="subtitle1" sx={{ color: cmmsColors.text, fontWeight: 700 }}>
          <FormattedMessage {...messages.documentsCount} values={{ count: documents.length }} />
        </Typography>
        <HideIfNoRole anyOf={CMMS_FIELD_ROLES}>
          <Button
            variant="contained"
            size="small"
            startIcon={<CloudUploadIcon />}
            onClick={() => setIsUploadOpen(true)}
            sx={getContainedButtonSx(cmmsColors)}
          >
            <FormattedMessage {...messages.documentsUploadButton} />
          </Button>
        </HideIfNoRole>
      </Stack>

      {documentsQuery.isLoading &&
        <CmmsListSkeleton rows={3} height={28} />
      }

      {documentsQuery.error &&
        <CmmsErrorState error={documentsQuery.error} fallback={operationFailed} onRetry={() => documentsQuery.refetch()} isRetrying={documentsQuery.isFetching} dense />
      }

      {!documentsQuery.isLoading && !documentsQuery.error && documents.length === 0 &&
        <Typography variant="body2" sx={{ color: cmmsColors.mutedText, fontStyle: "italic" }}>
          <FormattedMessage {...messages.documentsEmpty} />
        </Typography>
      }

      {documents.length > 0 &&
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: cmmsColors.mutedText }}><FormattedMessage {...messages.fieldTitle} /></TableCell>
              <TableCell sx={{ color: cmmsColors.mutedText }}><FormattedMessage {...messages.fieldType} /></TableCell>
              <TableCell sx={{ color: cmmsColors.mutedText }}><FormattedMessage {...messages.documentsColumnSize} /></TableCell>
              <TableCell sx={{ color: cmmsColors.mutedText }}><FormattedMessage {...messages.documentsColumnUploadedBy} /></TableCell>
              <TableCell sx={{ color: cmmsColors.mutedText }}><FormattedMessage {...messages.documentsColumnUploadedAt} /></TableCell>
              <TableCell sx={{ color: cmmsColors.mutedText, width: 90 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((doc) => {
              const badge = getAssetDocumentTypeBadge(doc.documentType, intl);
              const FileIcon = getFileIcon(doc.contentType);
              const uploader = userNameById.get(doc.uploadedByUserId) ?? `#${doc.uploadedByUserId}`;
              return (
                <TableRow
                  key={doc.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleDownload(doc)}
                  title={intl.formatMessage(messages.documentsDownloadTitle, { fileName: doc.fileName })}
                >
                  <TableCell sx={{ color: cmmsColors.text }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FileIcon fontSize="small" sx={{ color: cmmsColors.mutedText }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          <Typography variant="body2" sx={{ color: cmmsColors.text, fontWeight: 700 }}>
                            {doc.title}
                          </Typography>
                          {doc.id === overviewImageDocumentId &&
                            <Chip
                              label={intl.formatMessage(messages.overviewImageChip)}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, color: cmmsColors.text, borderColor: cmmsColors.border }}
                            />
                          }
                        </Box>
                        <Typography variant="caption" sx={{ color: cmmsColors.mutedText, wordBreak: "break-all" }}>
                          {doc.fileName}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={badge.label}
                      size="small"
                      sx={{ backgroundColor: badge.bg, color: badge.text, fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: cmmsColors.text, whiteSpace: "nowrap" }}>
                    {formatFileSize(doc.fileSize)}
                  </TableCell>
                  <TableCell sx={{ color: cmmsColors.text }}>{uploader}</TableCell>
                  <TableCell sx={{ color: cmmsColors.text, whiteSpace: "nowrap" }}>
                    {formatTimestamp(intl, doc.uploadedAt)}
                  </TableCell>
                  {/* stopPropagation keeps these buttons from also firing the row's download. */}
                  <TableCell sx={{ whiteSpace: "nowrap" }} onClick={(event) => event.stopPropagation()}>
                    <HideIfNoRole anyOf={CMMS_FIELD_ROLES}>
                      <IconButton
                        size="small"
                        aria-label={intl.formatMessage(messages.documentsEditAria)}
                        disabled={isMutating}
                        onClick={() => setEditTarget(doc)}
                        sx={{ color: cmmsColors.mutedText }}
                      >
                        <EditIcon fontSize="inherit" />
                      </IconButton>
                    </HideIfNoRole>
                    <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
                      <IconButton
                        size="small"
                        aria-label={intl.formatMessage(messages.documentsDeleteAria)}
                        disabled={isMutating}
                        onClick={() => handleDelete(doc)}
                        sx={{ color: cmmsColors.mutedText }}
                      >
                        <DeleteOutlineIcon fontSize="inherit" />
                      </IconButton>
                    </HideIfNoRole>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      }

      <AssetDocumentUploadDialog
        assetNodeId={assetNodeId}
        companyKey={companyKey}
        open={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
      />

      <AssetDocumentEditDialog
        assetDocument={editTarget}
        assetNodeId={assetNodeId}
        companyKey={companyKey}
        open={Boolean(editTarget)}
        onClose={() => setEditTarget(null)}
      />

      <AxionControls.AxionConfirmation
        open={Boolean(confirmation)}
        title={confirmation?.title}
        message={confirmation?.message}
        confirmText={confirmation?.confirmText ?? intl.formatMessage(cmmsMessages.confirm)}
        cancelText={intl.formatMessage(cmmsMessages.cancel)}
        onConfirm={confirmation?.onConfirm}
        onCancel={() => setConfirmation(null)}
      />
      <AxionControls.AxionBackdrop open={isMutating} />
    </Box>
  );
}
